<template>
  <v-card class="my-1 mr-1">
    <v-list two-line>
      <v-list-item>
        <v-list-item-action>
          <div>
            <v-skeleton-loader
              v-if="!thumbnailPng"
              type="avatar"
              width="62px"
              height="62px"
            />
            <v-img
              v-else
              :src="thumbnailPng"
              width="62px"
              height="62px"
              alt="thumbnail"
              :class="{
                'blip': isStepGeneratingPreviewComplete,
                'part-thumbnail': isStepGeneratingPreviewComplete
              }"
              contain
              @click.stop="() => {
                if (isStepGeneratingPreviewComplete) {
                  $emit('thumbnail:clicked', part);
                }
              }"
            />
          </div>
          <div
            v-show="isStepGeneratingPreviewComplete"
            style="width: 100%;"
            class="text-center caption"
          >
            <v-icon small>
              mdi-arrow-up-thick
            </v-icon>
            <br>
            Click for
            <br>
            3D Preview
          </div>
        </v-list-item-action>

        <v-list-item-content class="pt-0">
          <v-list-item-title class="pt-0">
            <v-stepper
              :value="currentStepNumber"
              class="elevation-0"
              style="max-height: 36px; white-space: nowrap;"
            >
              <v-stepper-header style="max-height: 36px;">
                <v-stepper-step
                  :complete="isStepUploadComplete"
                  step="1"
                  style="height: 36px;"
                  class="py-0"
                >
                  <div style="display: flex;" class="body-2">
                    {{ stepUploadStatusMessage }}
                    <div
                      v-if="isStepUploadInProgress"
                      class="loading"
                      style="width: 16px;"
                    />
                  </div>
                </v-stepper-step>

                <v-divider />

                <!-- TODO put back in stepper vvv -->
                <v-stepper-step
                  style="height: 36px;"
                  class="py-0"
                  :complete="isStepCheckManufacturabilityComplete"
                  :rules="[() =>
                    !(!_.get(partStatus, 'isManufacturable') && !!_.get(partStatus, 'isManufacturableMsg'))
                  ]"
                  step="2"
                >
                  <div style="display: flex;" class="body-2">
                    {{ stepCheckManufacturabilityStatusMessage }}
                    <div
                      v-if="isStepCheckManufacturabilityInProgress"
                      class="loading"
                      style="width: 16px;"
                    />
                  </div>
                  <small v-if="!!_.get(partStatus, 'isManufacturableMsg')">
                    {{ _.get(partStatus, 'isManufacturableMsg') }}
                  </small>
                </v-stepper-step>

                <v-divider />

                <v-stepper-step style="height: 36px;" class="py-0" step="3">
                  (Coming soon)
                </v-stepper-step>
              </v-stepper-header>
            </v-stepper>
          </v-list-item-title>

          <v-row class="px-1 pb-0 mt-5" dense>
            <v-col class="pa-0">
              <div style="display: flex; flex-direction: column; align-items: baseline;">
                <div>
                  {{ _.get(part, 'CustItemName', '') }}
                  <v-btn
                    icon
                    text
                    small
                    :class="{ hidden: !isStepPartCreationComplete }"
                  >
                    <v-icon small>
                      mdi-pencil
                    </v-icon>
                  </v-btn>
                </div>

                <!-- Done this way because ReqQty comes as a stringified float (needs to be int) -->
                <v-text-field
                  :value="parseInt(part.ReqQty)"
                  :rules="[v => v > 0]"
                  :disabled="isCalculatingCost"
                  label="Quantity"
                  type="number"
                  style="width: 80px"
                  hide-details
                  @input="part.ReqQty = parseInt($event);
                          part.EstimateLineStatus.isNestingCalculated = false;
                          part.EstimateLineStatus.isCuttingCalculated = false;
                  "
                />

                <v-spacer />
              </div>
            </v-col>

            <v-col v-if="isExpanded" class="text-center">
              <h4 class="ma-0 pa-0">
                Please {{ partsOptionsMessage }}...
              </h4>
            </v-col>

            <v-col class="text-right">
              <span v-if="!isExpanded && uploadProgress < 100">{{ status }}</span>
            </v-col>
          </v-row>

          <v-progress-linear
            v-if="!isExpanded && uploadProgress < 100"
            rounded
            :value="uploadProgress"
          />
        </v-list-item-content>

        <v-list-item-action>
          <v-btn
            :class="{ hidden: !isStepPartCreationComplete }"
            style="position:absolute; bottom: 60%;"
            icon
            @click="toggleExpanded"
          >
            <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>

          <v-btn
            v-if="!isExpanded"
            text
            style="position:absolute; bottom: 0;"
            color="error"
            @click="part.remove()"
          >
            Delete
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <v-expand-transition>
      <div v-show="isExpanded">
        <v-card-text>
          <v-container>
            <v-row justify="space-around">
              <v-col class="text-center" cols="5">
                <span class="subtitle-2">Material</span>
                <v-radio-group
                  v-model="part.Material"
                  :disabled="isCalculatingCost"
                  class="text-center"
                  row
                >
                  <v-radio
                    v-for="(material, i) in materials"
                    :key="i"
                    :value="material.RowPointer"
                    :label="material.Description"
                    @change="part.MaterialSeries = '';
                             part.MaterialThickness = '';
                             part.MaterialFinish = '';
                             part.MaterialFinishOption = ''
                             part.EstimateLineStatus.isNestingCalculated = false;
                             part.EstimateLineStatus.isCuttingCalculated = false;
                    "
                  />
                </v-radio-group>
              </v-col>
              <v-col class="text-center" cols="5">
                <span class="subtitle-2">Series</span>
                <v-radio-group
                  v-model="part.MaterialSeries"
                  :disabled="isCalculatingCost"
                  class="text-center"
                  row
                >
                  <v-radio
                    v-for="(serie, i) in series"
                    :key="i"
                    :label="serie.Description.replace(getMaterialDescription(part.Material), '')"
                    :value="serie.RowPointer"
                    @change="part.MaterialThickness = '';
                             part.MaterialFinish = '';
                             part.MaterialFinishOption = '';
                             part.EstimateLineStatus.isNestingCalculated = false;
                             part.EstimateLineStatus.isCuttingCalculated = false;
                             selectClosestAvailableThickness();
                    "
                  />
                </v-radio-group>
              </v-col>
              <v-col class="text-center" cols="2">
                <span class="subtitle-2">
                  Thickness
                  <span v-if="detectedThickness">
                    (Detected {{ detectedThickness }}mm)
                  </span>
                </span>
                <div style="display: flex; justify-content: center;">
                  <v-select
                    v-model="part.MaterialThickness"
                    :items="thicknesses"
                    :disabled="isCalculatingCost"
                    item-text="Description"
                    item-value="_id"
                    style="width:120px;"
                    @change="fetchFinishes({
                               query: {
                                 Item: selectedThickness.Item,
                               }
                             });
                             part.MaterialFinish = '';
                             part.MaterialFinishOption = '';
                             part.EstimateLineStatus.isNestingCalculated = false;
                             part.EstimateLineStatus.isCuttingCalculated = false;
                    "
                  />
                </div>
              </v-col>
            </v-row>
            <v-row v-if="finishes.length > 0" justify="space-around">
              <transition name="slide-fade">
                <v-col class="text-center" cols="auto">
                  <span class="subtitle-2">Finish</span>
                  <v-progress-circular
                    v-if="isFinishesFindPending"
                    indeterminate
                    color="primary"
                    size="16"
                  />
                  <v-radio-group
                    v-model="part.MaterialFinish"
                    :disabled="isCalculatingCost"
                    row
                    @change="part.MaterialFinishOption = '';
                             part.EstimateLineStatus.isNestingCalculated = false;
                             part.EstimateLineStatus.isCuttingCalculated = false;"
                  >
                    <v-radio
                      v-for="(finish, i) in finishes"
                      :key="i"
                      :label="finish.Description"
                      :value="finish.RowPointer"
                      :disabled="!isFinishAvailable(finish._id)"
                    />
                  </v-radio-group>
                </v-col>
              </transition>
              <transition name="slide-fade">
                <v-col v-if="options.length > 0" class="text-center" cols="auto">
                  <span class="subtitle-2">Option</span>
                  <v-select
                    v-model="part.MaterialFinishOption"
                    :items="options"
                    :disabled="isCalculatingCost"
                    item-text="Description"
                    item-value="_id"
                    style="width:400px;"
                    @change="part.EstimateLineStatus.isNestingCalculated = false;
                             part.EstimateLineStatus.isCuttingCalculated = false;"
                  />
                </v-col>
              </transition>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <div style="width: 100%;">
            <v-list two-line>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    <div style="display: flex; align-items: center;">
                      <v-btn
                        :disabled="!isStepCheckManufacturabilityComplete"
                        text
                        @click="$emit('thumbnail:clicked', part);"
                      >
                        Analysis
                      </v-btn>

                      <v-spacer />

                      <v-btn
                        text
                        color="error"
                        @click="part.remove()"
                      >
                        Delete
                      </v-btn>

                      <v-btn
                        text
                        :disabled="!canCalculateCost"
                        :loading="isCalculatingCost"
                        @click="$emit('calculatecost', part)"
                      >
                        {{ part.isCsi ? 'Recalculate Cost' : 'Calculate Cost' }}
                      </v-btn>
                    </div>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-card-actions>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
export default {
  name: 'OLDUploadPartCard',
};
</script>

<style scoped>

</style>
